'use client';

import React, { useEffect, useLayoutEffect, useState } from 'react';

// The overlay must never be visible without its document-level listeners: a
// click in that gap falls through to the page instead of selecting an area.
// A layout effect attaches them in the same commit that shows the overlay
// (a passive effect can run a task later). Server render never activates the
// picker, so fall back to useEffect there to avoid the SSR warning.
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export interface FeedbackSelectionRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FeedbackSelection {
  description: string;
  rect: FeedbackSelectionRect;
}

interface FeedbackAreaPickerProps {
  active: boolean;
  onCancel: () => void;
  onSelect: (selection: FeedbackSelection) => void;
}

const normaliseText = (value: string | null | undefined) =>
  value?.replace(/\s+/g, ' ').trim() || '';

const quoted = (value: string) => `“${value.slice(0, 120)}”`;

function labelledByText(element: HTMLElement): string {
  const ids = normaliseText(element.getAttribute('aria-labelledby')).split(' ').filter(Boolean);
  return normaliseText(ids.map((id) => document.getElementById(id)?.textContent).join(' '));
}

function elementName(element: HTMLElement): string {
  const feedbackLabel = normaliseText(element.getAttribute('data-feedback-label'));
  if (feedbackLabel) return feedbackLabel;

  const ariaLabel = normaliseText(element.getAttribute('aria-label'));
  if (ariaLabel) return ariaLabel;

  const labelledBy = labelledByText(element);
  if (labelledBy) return labelledBy;

  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement) {
    const label = element.labels ? normaliseText(Array.from(element.labels).map((item) => item.textContent).join(' ')) : '';
    if (label) return label;
    if (element instanceof HTMLInputElement && ['button', 'submit', 'reset'].includes(element.type)) {
      return normaliseText(element.value);
    }
    return normaliseText(element.getAttribute('placeholder') || element.getAttribute('name'));
  }

  if (element instanceof HTMLImageElement) return normaliseText(element.alt);
  return normaliseText(element.textContent || element.getAttribute('title'));
}

function implicitRole(element: HTMLElement): string {
  const explicitRole = normaliseText(element.getAttribute('role'));
  if (explicitRole) return explicitRole.replace(/-/g, ' ');

  const tag = element.tagName.toLowerCase();
  if (tag === 'a' && element.hasAttribute('href')) return 'link';
  if (tag === 'button') return 'button';
  if (tag === 'textarea') return 'text box';
  if (tag === 'select') return 'select';
  if (tag === 'img') return 'image';
  if (/^h[1-6]$/.test(tag)) return 'heading';
  if (tag === 'nav') return 'navigation';
  if (tag === 'main') return 'main content';
  if (tag === 'header') return 'header';
  if (tag === 'footer') return 'footer';
  if (element instanceof HTMLInputElement) {
    if (element.type === 'checkbox') return 'checkbox';
    if (element.type === 'radio') return 'radio button';
    if (element.type === 'button' || element.type === 'submit') return 'button';
    return 'input';
  }
  return tag === 'div' || tag === 'span' ? 'page area' : tag.replace(/-/g, ' ');
}

function closestNamedContext(element: HTMLElement): string {
  const landmark = element.closest<HTMLElement>('nav, main, header, footer, section, form, [role="region"]');
  if (!landmark || landmark === element) return '';
  const name = elementName(landmark);
  if (name && name.length <= 80) return name;

  const heading = landmark.querySelector<HTMLElement>('h1, h2, h3, h4, h5, h6');
  return normaliseText(heading?.textContent).slice(0, 80);
}

const feedbackElement = (target: Element): HTMLElement | null => {
  const semanticTarget = target.closest(
    '[data-feedback-label], button, a[href], input, textarea, select, [role], h1, h2, h3, h4, h5, h6, img',
  );
  if (semanticTarget instanceof HTMLElement) return semanticTarget;
  if (target instanceof HTMLElement) return target;
  let parent: Element | null = target.parentElement;
  while (parent && !(parent instanceof HTMLElement)) parent = parent.parentElement;
  return parent instanceof HTMLElement ? parent : null;
};

/**
 * Build support-friendly, stable target copy from accessible semantics. Raw
 * CSS classes are deliberately excluded because generated utility strings do
 * not identify the control and change whenever presentation changes.
 */
export function describeFeedbackTarget(target: Element): string {
  const element = feedbackElement(target);
  if (!element) return 'Page area';
  const role = implicitRole(element);
  const name = elementName(element);
  const context = closestNamedContext(element);

  if (name) return `${role} ${quoted(name)}`;
  if (context) return `${role} in ${quoted(context)}`;
  if (element.id) return `${role} ${quoted(element.id)}`;
  return role === 'page area' ? 'Page area' : `${role.charAt(0).toUpperCase()}${role.slice(1)}`;
}

function selectionFor(element: Element): FeedbackSelection {
  const target = feedbackElement(element) || element;
  const rect = target.getBoundingClientRect();
  return {
    description: describeFeedbackTarget(element),
    rect: {
      x: Math.max(0, rect.left + window.scrollX),
      y: Math.max(0, rect.top + window.scrollY),
      width: Math.max(1, rect.width),
      height: Math.max(1, rect.height),
    },
  };
}

const FeedbackAreaPicker: React.FC<FeedbackAreaPickerProps> = ({ active, onCancel, onSelect }) => {
  const [highlight, setHighlight] = useState<DOMRect | null>(null);

  useBrowserLayoutEffect(() => {
    if (!active) return;

    const ignored = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest('[data-feedback-picker-ignore]'));

    const handlePointerMove = (event: PointerEvent) => {
      if (ignored(event.target) || !(event.target instanceof Element)) {
        setHighlight(null);
        return;
      }
      setHighlight((feedbackElement(event.target) || event.target).getBoundingClientRect());
    };

    const handlePressStart = (event: Event) => {
      if (ignored(event.target)) return;
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    const handleClick = (event: MouseEvent) => {
      if (ignored(event.target) || !(event.target instanceof Element)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setHighlight(null);
      onSelect(selectionFor(event.target));
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        setHighlight(null);
        onCancel();
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const target = document.activeElement;
      if (!(target instanceof Element) || target === document.body || ignored(target)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      setHighlight(null);
      onSelect(selectionFor(target));
    };

    document.addEventListener('pointermove', handlePointerMove, true);
    document.addEventListener('pointerdown', handlePressStart, true);
    document.addEventListener('pointerup', handlePressStart, true);
    document.addEventListener('mousedown', handlePressStart, true);
    document.addEventListener('mouseup', handlePressStart, true);
    document.addEventListener('touchstart', handlePressStart, { capture: true, passive: false });
    document.addEventListener('touchend', handlePressStart, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove, true);
      document.removeEventListener('pointerdown', handlePressStart, true);
      document.removeEventListener('pointerup', handlePressStart, true);
      document.removeEventListener('mousedown', handlePressStart, true);
      document.removeEventListener('mouseup', handlePressStart, true);
      document.removeEventListener('touchstart', handlePressStart, true);
      document.removeEventListener('touchend', handlePressStart, true);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [active, onCancel, onSelect]);

  if (!active) return null;

  return (
    <>
      {highlight && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-[59] rounded border-2 border-blue-500 bg-blue-500/10 shadow-[0_0_0_2px_rgba(255,255,255,0.9)]"
          style={{ left: highlight.left, top: highlight.top, width: highlight.width, height: highlight.height }}
        />
      )}
      <div
        data-feedback-picker-ignore
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-2 top-2 z-[60] mx-auto flex max-w-xl items-center justify-between gap-3 rounded-lg bg-gray-950 px-4 py-3 text-sm text-white shadow-xl"
      >
        <span>
          Select the problem area. Click or tap a control, or focus it with Tab and press Enter. Press Escape to cancel.
        </span>
        <button
          data-feedback-picker-ignore
          type="button"
          onClick={() => {
            setHighlight(null);
            onCancel();
          }}
          className="pointer-events-auto shrink-0 rounded border border-white/60 px-3 py-1.5 font-medium hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white"
          aria-label="Cancel area selection"
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default FeedbackAreaPicker;
